import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DetailTitleService } from './detail-title.service';
import { APP_TITLE_SUFFIX } from '../config';
import { CapitalizePipe } from '../pipes';

/**
 * Sole writer of the browser tab title.
 *
 * Two sources feed it: the activated route contributes a static translation key, and detail
 * pages contribute the entity they loaded. Rather than letting both call `Title.setTitle` and
 * race — route titles are applied after component activation, so whichever wrote last would
 * win depending on API latency — both write signals here and a single effect derives the
 * result. The detail title takes precedence, but only while it belongs to the navigation on
 * screen; anything older is ignored so a document number cannot leak onto the next page.
 */
@Injectable({
  providedIn: 'root',
})
export class PageTitleService {
  #title = inject(Title);
  #translate = inject(TranslateService);
  #capitalize = inject(CapitalizePipe);
  #detailTitle = inject(DetailTitleService);
  #suffix = inject(APP_TITLE_SUFFIX);

  /** Translation key declared on the activated route, set by `RcTitleStrategy`. */
  readonly routeTitleKey = signal<string | undefined>(undefined);

  /** Navigation the current {@link routeTitleKey} was resolved for. */
  readonly navId = signal(-1);

  /** Re-resolve keys when the user switches language. */
  #lang = toSignal(this.#translate.onLangChange, { initialValue: null });

  #detailText = computed(() =>
    this.#detailTitle.stamp() === this.navId() ? this.#detailTitle.text() : undefined,
  );

  #routeText = computed(() => {
    // Read inside this computed, not further down: memoization would otherwise keep serving
    // the previous language's translation, since the key itself has not changed.
    this.#lang();
    const key = this.routeTitleKey();
    return key ? this.#capitalize.transform(this.#translate.instant(key)) : undefined;
  });

  readonly title = computed(() => {
    const page = this.#detailText() ?? this.#routeText();
    return page ? `${page} | ${this.#suffix}` : this.#suffix;
  });

  constructor() {
    effect(() => this.#title.setTitle(this.title()));
  }
}
